/**
 * Tibbs's line library (lines.json), typed. Pure: no DOM, no audio.
 * docs/TIBBS-VOICE.md section 7.
 */
import raw from './lines.json';
import type { TibbsPose } from '../components/Art';

export type Trigger =
  | 'quiet'
  | 'legendary'
  | 'legendary40'
  | 'eurekaPair'
  | 'bonus2'
  | 'bonus3'
  | 'bonus4'
  | 'zero'
  | 'lowUnder'
  | 'outAndWon'
  | 'rushed'
  | 'hm'
  | 'ohDear'
  | 'caught'
  | 'bigPair'
  | 'patienceWon'
  | 'patienceLost'
  | 'halved'
  | 'reshuffle'
  | 'leadChange'
  | 'leaderAway'
  | 'closeRace'
  | 'tieLead'
  | 'comeback'
  | 'lastPlace'
  | 'runawayLast'
  | 'homeStretch'
  | 'member'
  | 'memberLeads'
  | 'playoff'
  | 'ceremonyOpen'
  | 'recap'
  | 'scenic'
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'twoPlayer'
  | 'goldShared'
  | 'ceremonyClose'
  | 'connector';

export interface Line {
  id: string;
  trigger: Trigger;
  text: string;
  /** Spoken text for the render, when it differs from the caption (pronunciation). */
  say?: string;
  tags: string[];
  pose: TibbsPose;
  weight: number;
}

interface RawLine {
  id: string;
  trigger: string;
  text: string;
  say?: string;
  tags?: string[];
  pose?: string;
  weight?: number;
}

export const LINES: readonly Line[] = (raw.lines as RawLine[]).map((l) => ({
  id: l.id,
  trigger: l.trigger as Trigger,
  text: l.text,
  ...(l.say ? { say: l.say } : {}),
  tags: l.tags ?? [],
  pose: (l.pose ?? 'wry') as TibbsPose,
  weight: l.weight ?? 1,
}));

export const LINE_BY_ID: ReadonlyMap<string, Line> = new Map(LINES.map((l) => [l.id, l]));

const byTrigger = new Map<Trigger, Line[]>();
for (const l of LINES) {
  const list = byTrigger.get(l.trigger) ?? [];
  list.push(l);
  byTrigger.set(l.trigger, list);
}

export function linesFor(t: Trigger): readonly Line[] {
  return byTrigger.get(t) ?? [];
}

/** Ceremony triggers speak names in the proclaim form; everything else is plain. */
export const CEREMONY_TRIGGERS: ReadonlySet<Trigger> = new Set([
  'ceremonyOpen',
  'recap',
  'scenic',
  'bronze',
  'silver',
  'gold',
  'twoPlayer',
  'goldShared',
  'ceremonyClose',
  'connector',
]);

/** Triggers that praise the subject (the roast-balance rule prefers these after two digs). */
export const PRAISE: ReadonlySet<Trigger> = new Set(['legendary', 'legendary40', 'bonus2', 'bonus3', 'bonus4', 'eurekaPair', 'outAndWon', 'zero', 'lowUnder', 'patienceWon']);

export const isDig = (l: Line) => l.tags.includes('dig');

/** The caption: names filled in, cues and the render-only bits removed. */
export function captionOf(l: Line, names: readonly (string | null | undefined)[]): string {
  let s = l.text.replace(/\{name2\}/g, names[1] ?? '').replace(/\{name\}/g, names[0] ?? '');
  s = s.replace(/\[[^\]]*\]/g, ' ');
  return tidy(s);
}

/** The caption with the names dropped (the nameless form). */
export function namelessCaption(l: Line): string {
  const s = l.text
    .replace(/\{name2?\}\.?/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ');
  return tidy(s).replace(/^And\.\s*/, '');
}

function tidy(s: string): string {
  return s
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?])/g, '$1')
    .replace(/^[\s.]+/, '')
    .trim();
}

/** Lines whose text names players (`{name}` or `{name2}`). */
export const hasName = (l: Line) => /\{name\}/.test(l.text);
